import { Component, inject, signal } from '@angular/core';
import { disabled, email, form, FormField, minLength, pattern, required, submit, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Person } from '@models';
import { NotificationService } from '@services';
import { hasValidChecksum } from '@validators';

@Component({
  selector: 'app-dataset-form-person',
  templateUrl: './person.component.html',
  styleUrls: ['./person.component.scss'],
  imports: [
    FormField,

    MatProgressSpinnerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatOptionModule,
    MatSelectModule,
  ],
})
export class DatasetFormPersonComponent {
  dialog = inject<MatDialogRef<DatasetFormPersonComponent>>(MatDialogRef);
  person = inject<Person>(MAT_DIALOG_DATA);
  notificationService = inject(NotificationService);

  pending = signal(false);

  personForm = form(signal({
    firstName: this.person?.firstName || "",
    lastName: this.person?.lastName || "",
    organisation: this.person?.organisation || "",
    roles: this.person?.roles || [] as string[],
    email: this.person?.email || "",
    orcid: this.person?.orcid || "",
    url: this.person?.url || "",
  }), (schemaPath) => {
    required(schemaPath.firstName);
    required(schemaPath.lastName);
    required(schemaPath.roles);
    required(schemaPath.email, {
      when: () => {
        const roles = this.personForm.roles().value();
        return roles.includes('pointOfContact')
      }
    });

    minLength(schemaPath.roles, 1);

    email(schemaPath.email);

    pattern(schemaPath.orcid, /^(\d{4}-){3}\d{3}(\d|X)$/);
    pattern(schemaPath.url, /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#()?&//=]*)$/);

    validate(schemaPath.orcid, ({ value }) => {
      const orcid = value();
      if (orcid && !hasValidChecksum(orcid)) {
        return {
          kind: 'orcid',
          message: 'Invalid checksum',
        };
      }
      return null;
    });

    disabled(schemaPath.orcid, { when: this.pending });
    disabled(schemaPath.firstName, { when: this.pending });
    disabled(schemaPath.lastName, { when: this.pending });
    disabled(schemaPath.email, { when: this.pending });
    disabled(schemaPath.organisation, { when: this.pending });
  });

  public submit(event: Event) {
    event.preventDefault();

    submit(this.personForm, async () => {
      this.dialog.close({
        firstName: this.personForm.firstName().value() || undefined,
        lastName: this.personForm.lastName().value() || undefined,
        organisation: this.personForm.organisation().value() || undefined,
        roles: this.personForm.roles().value(),
        email: this.personForm.email().value() || undefined,
        orcid: this.personForm.orcid().value() || undefined,
      });
    });
  }

  lookup() {
    const orcid = this.personForm.orcid().value()

    this.pending.set(true);

    fetch(`https://pub.orcid.org/v3.0/${orcid}/record`, {
      headers: {
        Accept: 'application/json',
      }
    }).then((response) => {
      if (!response.ok) {
        throw new Error("response is not ok");
      }
      return response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).then((result: any) => {
      const givenName = result['person']?.["name"]?.["given-names"]?.["value"];
      this.personForm.firstName().value.set(givenName || '');

      const lastName = result['person']?.["name"]?.["family-name"]?.["value"];
      this.personForm.lastName().value.set(lastName || '');

      const mail = result['person']?.["emails"]?.["email"]?.[0]?.["email"];
      this.personForm.email().value.set(mail || '');

      const org = result['activities-summary']?.["employments"]?.["affiliation-group"]?.[0]?.["summaries"]?.[0]?.['employment-summary']?.['organization']?.['name'];
      this.personForm.organisation().value.set(org || '');
    }).catch(err => {
      console.error(err);
      this.notificationService.error(`Failed to search the ORCID registry.`);
    }).finally(() => {
      this.pending.set(false);
    });
  }
}
